import { FormHelperText } from "@mui/material";
import { City, State } from "country-state-city";
import React, { useEffect } from "react";
import { RegOrderErrs } from "../../stateManagement/reducers/ordersReducer";
import { SiteLevel } from "../../types/SiteLevel";
import AddressAutoComplete from "../AddressAutoComplete/AddressAutoComplete";

interface LocationPickerProps {
  countryCode?: string;
  level?: SiteLevel;
  provinces?: string[];
  province: string;
  city: string;
  address: string;
  errors?: RegOrderErrs;
  setProvince: (province: string) => void;
  setCity: (city: string) => void;
  setAddress: (address: string) => void;
  onChange?: (field: string) => void;
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  countryCode = "NG",
  level,
  provinces,
  province,
  city,
  address,
  setProvince,
  setCity,
  setAddress,
  onChange,
  errors,
}) => {
  const states = State.getStatesOfCountry(countryCode).filter((state) =>
    provinces ? provinces.includes(state.isoCode) : true,
  );

  // Fetch cities dynamically when a province is picked
  const cities = province ? City.getCitiesOfState(countryCode, province) : [];

  useEffect(() => {
    if (onChange) {
      onChange("address");
    }
  }, [address]);

  return (
    <div className="flex flex-col gap-4 max-w-full">
      {/* Province / Territory Select */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          State
        </label>
        <select
          value={province}
          onChange={(e) => {
            setProvince(e.target.value);
            setCity(""); // Reset city when province changes
            if (onChange) {
              onChange("province");
              onChange("city");
            }
          }}
          className={`w-full border p-2 rounded-md  ${errors?.state ? "border-error" : ""} active:outline-primary focus:outline-primary`}
        >
          <option value="">Select State</option>
          {states.map((prov) => (
            <option key={prov.isoCode} value={prov.isoCode}>
              {prov.name} ({prov.isoCode})
            </option>
          ))}
        </select>
        {errors?.state && (
          <FormHelperText error id="helper-text-state">
            {errors.state}
          </FormHelperText>
        )}
      </div>

      {/* City Select */}
      {(!level || level === SiteLevel.ADDRESS || level === SiteLevel.CITY) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City
          </label>
          <select
            value={city}
            onChange={(e) => {
              setCity(e.target.value);
              if (onChange) {
                onChange("city");
              }
            }}
            disabled={!province}
            className={`w-full border p-2 rounded-md disabled:bg-gray-100 ${errors?.city ? "border-error" : ""} active:outline-primary focus:outline-primary`}
          >
            <option value="">Select City</option>
            {cities.map((city) => (
              <option key={city.name} value={city.name}>
                {city.name}
              </option>
            ))}
          </select>
          {errors?.city && (
            <FormHelperText error id="helper-text-city">
              {errors.city}
            </FormHelperText>
          )}
        </div>
      )}
      {/* Street Address Input */}
      {(!level || level === SiteLevel.ADDRESS) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Street Address
          </label>
          <AddressAutoComplete
            address={address}
            setAddress={setAddress}
            error={errors?.address}
            countryCode={countryCode}
            stateName={province}
            cityName={city}
            onSelectAddress={() => {}}
          />
          {errors?.address && (
            <FormHelperText error id="helper-text-address">
              {errors.address}
            </FormHelperText>
          )}
        </div>
      )}
    </div>
  );
};
